import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader, StatCard } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Check, X, Phone } from "lucide-react";

export default function SmsConsentPage() {
  const { propertyId } = useProperty();
  const [data, setData] = useState({ residents: [], log: [], summary: {} });
  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/sms-consent?property_id=${propertyId}`);
        setData(data);
      } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/SmsConsentPage.jsx: swallowed', err); }
    })();
  }, [propertyId]);

  const s = data.summary || {};
  return (
    <div>
      <PageHeader
        eyebrow="Compliance"
        title="SMS Consent"
        subtitle="Track opt-ins per resident so every text broadcast is TCPA-safe."
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard label="Total residents" value={s.total || 0} icon={Users} accent="zinc" />
        <StatCard label="Opted in to SMS" value={s.opted_in || 0} accent="green" icon={Check} />
        <StatCard label="Not consented" value={s.opted_out || 0} accent="red" icon={X} />
      </div>

      <Card className="bg-card border-white/10 overflow-hidden mb-6">
        <div className="p-4 border-b border-white/10 overline">Consent status</div>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Resident</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Consent</TableHead>
              <TableHead>Last change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.residents.map((r) => (
              <TableRow key={r.id} className="border-white/5" data-testid={`consent-row-${r.id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell>{r.unit || "—"}</TableCell>
                <TableCell className="font-mono text-xs flex items-center gap-1">
                  {r.phone ? <><Phone className="h-3 w-3 text-zinc-500" /> {r.phone}</> : <span className="text-zinc-500">—</span>}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={r.consent
                    ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                    : "border-white/10 text-zinc-400"}>
                    {r.consent ? "Opted in" : "Not opted in"}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-zinc-500">
                  {r.consent_at ? new Date(r.consent_at).toLocaleString() : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="bg-card border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10 overline">Audit log</div>
        {data.log.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No consent changes recorded yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead>When</TableHead>
                <TableHead>Resident</TableHead>
                <TableHead>Change</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.log.map((e) => (
                <TableRow key={e.id} className="border-white/5">
                  <TableCell className="text-xs">{new Date(e.at).toLocaleString()}</TableCell>
                  <TableCell>{e.user_name || e.user_email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={e.consent
                      ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                      : "border-rose-500/40 text-rose-300 bg-rose-500/10"}>
                      {e.consent ? "Opted in" : "Opted out"}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{e.phone || "—"}</TableCell>
                  <TableCell className="text-xs text-zinc-500">{e.source}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
